import { effectScope, isRef, watch } from "vue";
import { db } from "../../../db.js";
import { resolveSettings } from "./helpChatSettings.mjs";
import {
  VIEWING_TTL_MS,
  absorbReplies,
  completedReplies,
  isErrorReply,
  latestUnread,
  loadUnreadState,
  parseViewing,
  saveUnreadState,
  storageKey,
  viewingKey,
} from "./helpChatUnread.mjs";

const LEASE_INTERVAL_MS = 2000;

let active = null;

function readVisible(visible) {
  if (isRef(visible)) return visible.value === true;
  if (typeof visible === "function") return visible() === true;
  return visible === true;
}

function windowToken() {
  return `help-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function sameTarget(left, right) {
  return left?.conversationId === right?.conversationId && left?.messageId === right?.messageId;
}

/**
 * Watch the signed-in user's conversations and raise a toast plus an unread
 * count when a reply finishes out of view. The count is kept in the store and
 * in localStorage so it survives navigation and a refresh.
 */
export function startHelpChatAlerts({ store, toast, userId, visible, openChat }) {
  stopHelpChatAlerts();
  if (!userId) return;

  const id = windowToken();
  const leaseKey = viewingKey(userId);
  const toasted = new Set();
  let memory = loadUnreadState(localStorage, userId);
  let latestList;
  const conversations = db.ref(`HelpChats/${userId}/Conversations`);
  const settings = db.ref(`HelpChats/${userId}/Settings`);
  const scope = effectScope();

  store.commit("save", {
    helpChatUnread: memory.unread.length,
    helpChatUnreadTarget: null,
  });

  function locallyVisible() {
    return readVisible(visible);
  }

  function chatIsVisible(now = Date.now()) {
    if (locallyVisible()) return true;
    const lease = parseViewing(localStorage.getItem(leaseKey), now, VIEWING_TTL_MS);
    return Boolean(lease && lease.id !== id);
  }

  function refreshLease() {
    if (locallyVisible()) {
      localStorage.setItem(leaseKey, JSON.stringify({ id, at: Date.now() }));
      return;
    }
    const lease = parseViewing(localStorage.getItem(leaseKey), Date.now(), VIEWING_TTL_MS);
    if (lease?.id === id) localStorage.removeItem(leaseKey);
  }

  function showToast(item) {
    if (toasted.has(item.key)) return;
    toasted.add(item.key);
    const name = resolveSettings(store.state.helpChatSettings).BotName;
    const failed = isErrorReply(item.message);
    const open = () => {
      store.commit("save", {
        helpChatFocus: {
          conversationId: item.conversationId,
          messageId: item.messageId,
        },
      });
      openChat();
    };
    const options = { onClick: open };
    if (failed) toast.error(`${name} could not reply`, options);
    else toast.info(`${name} has replied`, options);
  }

  function publish(list, { storeList = false } = {}) {
    latestList = list;
    const stored = loadUnreadState(localStorage, userId);
    const base = stored.primed ? stored : memory;
    const messages = completedReplies(list);
    const visibleNow = chatIsVisible();
    const { state, arrivals } = absorbReplies(base, messages, visibleNow);
    memory = state;
    saveUnreadState(localStorage, userId, state);
    const latest = latestUnread(messages, state.unread);
    const target = latest
      ? { conversationId: latest.conversationId, messageId: latest.messageId }
      : null;
    const patch = {};
    if (store.state.helpChatUnread !== state.unread.length) patch.helpChatUnread = state.unread.length;
    if (!sameTarget(store.state.helpChatUnreadTarget, target)) patch.helpChatUnreadTarget = target;
    if (storeList) patch.helpChatList = list;
    if (Object.keys(patch).length) store.commit("save", patch);
    if (visibleNow) return;
    for (const item of arrivals) showToast(item);
  }

  function onConversations(snapshot) {
    publish(snapshot.val(), { storeList: true });
  }

  function onSettings(snapshot) {
    store.commit("save", { helpChatSettings: snapshot.val() });
  }

  function onStorage(event) {
    if (event.key !== storageKey(userId) && event.key !== leaseKey) return;
    if (latestList === undefined) {
      memory = loadUnreadState(localStorage, userId);
      store.commit("save", { helpChatUnread: memory.unread.length });
      return;
    }
    publish(latestList);
  }

  conversations.on("value", onConversations, (error) => {
    console.error("[HelpChat] Could not watch conversations", error);
  });
  settings.on("value", onSettings, (error) => {
    console.error("[HelpChat] Could not watch settings", error);
  });
  window.addEventListener("storage", onStorage);
  refreshLease();
  scope.run(() => {
    watch(visible, () => {
      refreshLease();
      if (latestList !== undefined) publish(latestList);
    });
  });
  const timer = setInterval(() => {
    refreshLease();
    if (latestList !== undefined) publish(latestList);
  }, LEASE_INTERVAL_MS);

  active = {
    stop() {
      clearInterval(timer);
      scope.stop();
      window.removeEventListener("storage", onStorage);
      conversations.off("value", onConversations);
      settings.off("value", onSettings);
      const lease = parseViewing(localStorage.getItem(leaseKey), Date.now(), VIEWING_TTL_MS);
      if (lease?.id === id) localStorage.removeItem(leaseKey);
      if (active === this) active = null;
    },
  };
}

export function stopHelpChatAlerts() {
  active?.stop();
  active = null;
}
